import { useEffect, useLayoutEffect, useRef, type RefObject } from 'react';
import { wheelZoomSteps } from '../lib/zoom';

/**
 * Turn wheel gestures over `ref` into zoom steps.
 *
 * The listener is native and non-passive because React registers `onWheel`
 * passively, where `preventDefault` is ignored — the surface would zoom and
 * scroll at the same time. Preventing the default also claims ctrl+wheel, the
 * trackpad pinch gesture, from the browser's page zoom: over a zoomable
 * picture the pinch belongs to the picture.
 *
 * `onZoom` receives the (fractional, positive-is-in) step count and the raw
 * event, leaving each caller to derive a focal point in whatever coordinate
 * system it applies zoom in. It is read through a ref, so an inline closure
 * does not resubscribe the listener on every render.
 */
export function useWheelZoom(
  ref: RefObject<HTMLElement | null>,
  onZoom: (steps: number, event: WheelEvent) => void,
): void {
  const handler = useRef(onZoom);
  useLayoutEffect(() => {
    handler.current = onZoom;
  });

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const onWheel = (event: WheelEvent) => {
      event.preventDefault();
      handler.current(wheelZoomSteps(event.deltaY, event.deltaMode), event);
    };
    el.addEventListener('wheel', onWheel, { passive: false });
    return () => el.removeEventListener('wheel', onWheel);
  }, [ref]);
}
